import { FC, ReactNode } from 'react'
import { TrendBadge } from 'src/components/TrendBadge'
import { AdPerfOverview } from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import {
  ClickIcon,
  CoinIcon,
  CtrIcon,
  EngageIcon,
  EyeIcon,
  GenderIcon,
  SpendIcon,
  UsersIcon,
} from './icons'
import { compact, genderLabel, money, pct } from '../format'

interface Props {
  data?: AdPerfOverview
}

interface Card {
  Icon: FC
  foot: ReactNode
  title: string
  value: string
}

export const Scorecards: FC<Props> = ({ data }) => {
  if (!data) return <div className={s.scorecards} />
  const { totals: t, change, account, previous_window: prev } = data
  const trend = (key: keyof typeof change, lowerIsBetter = false) => (
    <>
      <TrendBadge
        lowerIsBetter={lowerIsBetter}
        previousFromDate={prev.since}
        previousToDate={prev.until}
        value={change[key]}
      />
      {change[key] !== null && change[key] !== undefined && (
        <span>vs previous period</span>
      )}
    </>
  )
  const share = (v?: number) =>
    v === undefined ? null : (
      <>
        <span className={s.shareBadge}>{v}%</span>
        <span>of impressions</span>
      </>
    )

  const cards: Card[] = [
    {
      Icon: SpendIcon,
      foot: trend('spend'),
      title: 'Total Spend',
      value: money(t.spend, account.currency),
    },
    {
      Icon: ClickIcon,
      foot: trend('clicks'),
      title: 'Total Clicks',
      value: compact(t.clicks),
    },
    {
      Icon: EyeIcon,
      foot: trend('impressions'),
      title: 'Impressions',
      value: compact(t.impressions),
    },
    { Icon: CtrIcon, foot: trend('ctr'), title: 'CTR', value: pct(t.ctr) },
    {
      Icon: EngageIcon,
      foot: trend('engagements'),
      title: 'Engagement',
      value: compact(t.engagements),
    },
    {
      Icon: UsersIcon,
      foot: share(data.top_age?.share),
      title: 'Top Age Group',
      value: data.top_age?.value ?? '–',
    },
    {
      Icon: GenderIcon,
      foot: share(data.top_gender?.share),
      title: 'Top Gender',
      value: data.top_gender ? genderLabel(data.top_gender.value) : '–',
    },
    {
      Icon: CoinIcon,
      foot: trend('cpc', true),
      title: 'Avg. CPC',
      value: money(t.cpc, account.currency, 2),
    },
  ]

  return (
    <div className={s.scorecards}>
      {cards.map((c) => (
        <div className={s.scorecard} key={c.title}>
          <div className={s.scorecardTitle}>
            <c.Icon />
            {c.title}
          </div>
          <div className={s.scorecardValue}>{c.value}</div>
          <div className={s.scorecardFoot}>{c.foot}</div>
        </div>
      ))}
    </div>
  )
}
